import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RackingInspectionData } from "../../screens/racking_inspection/types";

// Types
export interface RackingInspectionFilters {
  periodWeek: string;
  aisle: string;
  bay: string;
  level: string;
}

export interface RackingInspectionMetrics {
  completed: number;
  inProgress: number;
  total: number;
  currentPeriodCompletedInspections: number;
  currentWeekCompletedInspections: number;
}

interface RackingInspectionState {
  inspections: RackingInspectionData[];
  selectedFilters: RackingInspectionFilters;
  inspectionMetrics: RackingInspectionMetrics;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  activeTab: string;
  selectedInspection: RackingInspectionData | null;
  centralizedErrors: {
    errors: any[];
    activeError: any | null;
    isErrorModalVisible: boolean;
  };
}

const initialState: RackingInspectionState = {
  inspections: [],
  selectedFilters: {
    periodWeek: "",
    aisle: "",
    bay: "",
    level: "",
  },
  inspectionMetrics: {
    completed: 0,
    inProgress: 0,
    total: 0,
    currentPeriodCompletedInspections: 0,
    currentWeekCompletedInspections: 0,
  },
  loading: false,
  error: null,
  successMessage: null,
  activeTab: "walk-observations",

  selectedInspection: null,
  // Centralized errors for racking inspection module
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },
};

const rackingInspectionSlice = createSlice({
  name: "rackingInspection",
  initialState,
  reducers: {
    // Set inspections data
    setInspections: (state, action: PayloadAction<RackingInspectionData[]>) => {
      state.inspections = action.payload;
    },

    // Set selected filters
    setSelectedFilters: (
      state,
      action: PayloadAction<Partial<RackingInspectionFilters>>
    ) => {
      state.selectedFilters = { ...state.selectedFilters, ...action.payload };
    },

    // Set inspection metrics
    setInspectionMetrics: (
      state,
      action: PayloadAction<RackingInspectionMetrics>
    ) => {
      state.inspectionMetrics = action.payload;
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Set error message
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      if (action.payload) {
        state.loading = false;
      }
    },

    // Set active tab
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },

    // Set selected inspection
    setSelectedInspection: (
      state,
      action: PayloadAction<RackingInspectionData | null>
    ) => {
      state.selectedInspection = action.payload;
    },

    // Reset filters
    resetFilters: (state) => {
      state.selectedFilters = initialState.selectedFilters;
    },

    // Set success message
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },

    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // Centralized error handling
    addCentralizedError: (state, action: PayloadAction<any>) => {
      state.centralizedErrors.errors.push(action.payload);
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },

    showErrorModal: (state, action: PayloadAction<any>) => {
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
      state.centralizedErrors.errors.push(action.payload);
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },

    hideErrorModal: (state) => {
      state.centralizedErrors.isErrorModalVisible = false;
      state.centralizedErrors.activeError = null;
    },

    removeErrorById: (state, action: PayloadAction<string>) => {
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter((e: any) => e.id !== action.payload);
      if (state.centralizedErrors.activeError?.id === action.payload) {
        state.centralizedErrors.activeError = null;
        state.centralizedErrors.isErrorModalVisible = false;
      }
    },

    clearAllErrors: (state) => {
      state.centralizedErrors.errors = [];
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },
  },
});

export const {
  setInspections,
  setSelectedFilters,
  setInspectionMetrics,
  setLoading,
  setError,
  setActiveTab,
  setSelectedInspection,
  resetFilters,
  setSuccessMessage,
  clearSuccessMessage,
  // centralized error exports
  addCentralizedError,
  showErrorModal,
  hideErrorModal,
  removeErrorById,
  clearAllErrors,
} = rackingInspectionSlice.actions;

export default rackingInspectionSlice.reducer;
